/**
 * Welcome Agent
 * 
 * Responsible ONLY for:
 * - Saying welcome message in detected language
 * - Transitioning to FAQ Agent
 * 
 * NOTE: System instructions and tools are defined in backend (backend/config/agents.py)
 * This file only contains tool handlers, guardrails, and metadata.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

/**
 * Create the Welcome Agent node
 * System instructions and tools are provided by backend via ephemeral token
 */
export function createWelcomeAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  // Store timer reference for cleanup
  let autoTransitionTimer: number | null = null;
  
  return {
    id: 'welcome_agent',
    name: 'Welcome Agent',
    description: 'Says welcome message after language detection',
    
    // Initial prompt to trigger welcome message
    initialPrompt: 'Say welcome message in user\'s language. The system will automatically transition to FAQ.',
    
    // Requires language to be detected
    entryGuardrails: requireLanguage(),
    
    // Exit to FAQ agent
    exitGuardrails: {
      transitions: new Map([
        ['welcome_complete', {
          target: { agentId: 'faq_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['auto_transition', {
          target: { agentId: 'faq_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: undefined,
    },
    
    // Handle the proceed_to_faq tool call (if AI calls it manually)
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'proceed_to_faq') {
        console.log('[WelcomeAgent] Welcome complete (manual call), proceeding to FAQ');
        
        // Clear auto-transition timer since manual call happened
        if (autoTransitionTimer) {
          clearTimeout(autoTransitionTimer);
          autoTransitionTimer = null;
        }
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: 'Welcome complete, proceeding to FAQ' 
          },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: { language: context.transitionData.language },
        };
      }
      
      // Handle auto-transition trigger
      if (toolName === '__auto_transition__') {
        console.log('[WelcomeAgent] Auto-transition triggered');
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: 'Auto-transitioning to FAQ' 
          },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: { language: context.transitionData.language },
        };
      }
      
      // Unknown tool
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[WelcomeAgent] Activated with language:', context.transitionData.language);
      
      // Set up automatic transition after welcome message is spoken
      // Give the AI 4 seconds to speak the welcome message, then auto-transition
      console.log('[WelcomeAgent] Setting up auto-transition in 4 seconds...');
      
      autoTransitionTimer = window.setTimeout(() => {
        console.log('[WelcomeAgent] Auto-transition time reached - triggering transition to router');
        
        // Trigger a custom event that AgentManager can listen for
        const event = new CustomEvent('agent:auto-transition', {
          detail: {
            fromAgent: 'welcome_agent',
            toAgent: 'faq_agent',
            context: { language: context.transitionData.language }
          }
        });
        window.dispatchEvent(event);
        
        autoTransitionTimer = null;
      }, 6000); // 4 seconds - enough time for welcome message to be spoken
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[WelcomeAgent] Deactivated - transitioning to FAQ');
      
      // Clear any pending auto-transition timer
      if (autoTransitionTimer) {
        clearTimeout(autoTransitionTimer);
        autoTransitionTimer = null;
      }
    },
  };
}

export default createWelcomeAgent;
